/**
 * Online Users List Component
 *
 * Displays a list of currently online users with their presence status.
 * Integrates with CCMS SDK's PresenceHub for real-time updates.
 *
 * Features:
 * - Real-time presence updates via SignalR
 * - Filter by status (online, away, busy)
 * - Search users by name
 * - Click to view user profile
 * - Activity status indicators
 *
 * Usage:
 * ```tsx
 * <OnlineUsersList maxUsers={10} showSearch />
 * ```
 */

import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconSearch, IconUsers } from '@tabler/icons-react';
import { usePresence, useCCMS } from '@actschurch/ccms-sdk';
import type { UserPresenceUpdate } from '@actschurch/ccms-sdk';
import { PresenceDot } from './PresenceIndicator';

export interface OnlineUser {
  userId: string;
  email: string;
  firstName?: string;
  surname?: string;
  avatar?: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  lastActivity?: Date;
}

export interface OnlineUsersListProps {
  /** Maximum number of users to display */
  maxUsers?: number;
  /** Show search input */
  showSearch?: boolean;
  /** Filter by status */
  statusFilter?: 'online' | 'away' | 'busy' | 'all';
  /** Callback when user is clicked */
  onUserClick?: (userId: string) => void;
  /** Custom height */
  height?: number | string;
}

export const OnlineUsersList = React.memo(function OnlineUsersList({
  maxUsers,
  showSearch = false,
  statusFilter = 'all',
  onUserClick,
  height = 400,
}: OnlineUsersListProps) {
  const { getAllPresence } = usePresence();
  const { presence: presenceClient } = useCCMS();
  const [users, setUsers] = useState<OnlineUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Initial load of all presence data
  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        setLoading(true);
        const allPresence = await getAllPresence();
        const onlineOnly = allPresence.filter((p) => p.isOnline);

        // Map presence data to OnlineUser format
        const mappedUsers: OnlineUser[] = onlineOnly.map((presence) => ({
          userId: presence.userId,
          email: presence.userName || 'Unknown', // SDK uses userName not email
          firstName: presence.userName?.split(' ')[0],
          surname: presence.userName?.split(' ')[1],
          avatar: undefined, // Avatar not provided in UserPresenceUpdate
          status: presence.status.toLowerCase() as OnlineUser['status'],
          lastActivity: new Date(presence.lastSeen),
        }));

        setUsers(mappedUsers);
      } catch (error) {
        console.error('[OnlineUsersList] Failed to fetch online users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOnlineUsers();
  }, [getAllPresence]);

  // Real-time event subscriptions
  useEffect(() => {
    if (!presenceClient) return;

    const handleUserOnline = (update: UserPresenceUpdate) => {
      setUsers((prev) => {
        // Avoid duplicates
        if (prev.some((u) => u.userId === update.userId)) {
          return prev;
        }
        return [
          ...prev,
          {
            userId: update.userId,
            email: update.userName || 'Unknown',
            firstName: update.userName?.split(' ')[0],
            surname: update.userName?.split(' ')[1],
            avatar: undefined,
            status: update.status.toLowerCase() as OnlineUser['status'],
            lastActivity: new Date(update.lastSeen),
          },
        ];
      });
    };

    const handleUserOffline = (update: UserPresenceUpdate) => {
      setUsers((prev) => prev.filter((u) => u.userId !== update.userId));
    };

    const handleStatusChanged = (update: UserPresenceUpdate) => {
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === update.userId
            ? { ...u, status: update.status.toLowerCase() as OnlineUser['status'] }
            : u
        )
      );
    };

    presenceClient.on('userOnline', handleUserOnline);
    presenceClient.on('userOffline', handleUserOffline);
    presenceClient.on('statusChanged', handleStatusChanged);

    return () => {
      presenceClient.off('userOnline', handleUserOnline);
      presenceClient.off('userOffline', handleUserOffline);
      presenceClient.off('statusChanged', handleStatusChanged);
    };
  }, [presenceClient]);

  // Filter users by search query and status - MEMOIZED
  const filteredUsers = useMemo(() => {
    return users
      .filter((user) => {
        // Status filter
        if (statusFilter !== 'all' && user.status !== statusFilter) {
          return false;
        }

        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const displayName = `${user.firstName || ''} ${user.surname || ''}`.trim().toLowerCase();
          const email = user.email.toLowerCase();
          return displayName.includes(query) || email.includes(query);
        }

        return true;
      })
      .slice(0, maxUsers);
  }, [users, statusFilter, searchQuery, maxUsers]);

  const handleUserClick = (userId: string) => {
    if (onUserClick) {
      onUserClick(userId);
    }
  };

  if (loading) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed" ta="center">
          Loading online users...
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconUsers size={20} />
            <Title order={5}>Online Users</Title>
            <Badge size="sm" variant="light">
              {filteredUsers.length}
            </Badge>
          </Group>
        </Group>

        {/* Search */}
        {showSearch && (
          <TextInput
            placeholder="Search users..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            size="sm"
          />
        )}

        {/* Users List */}
        <ScrollArea h={height} type="auto">
          {filteredUsers.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No users online
            </Text>
          ) : (
            <Stack gap="xs">
              {filteredUsers.map((user) => (
                <UnstyledButton
                  key={user.userId}
                  onClick={() => handleUserClick(user.userId)}
                  style={{ width: '100%' }}
                >
                  <Paper
                    p="sm"
                    withBorder
                    style={{
                      cursor: onUserClick ? 'pointer' : 'default',
                      transition: 'background 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (onUserClick) {
                        e.currentTarget.style.background = 'var(--mantine-color-gray-light)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      {/* Avatar with presence dot */}
                      <Box style={{ position: 'relative' }}>
                        <Avatar
                          size="md"
                          radius="xl"
                          src={user.avatar}
                          color="indigo"
                        >
                          {((user.firstName?.[0] || '') + (user.surname?.[0] || '')).toUpperCase() || 'U'}
                        </Avatar>
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            background: 'var(--mantine-color-body)',
                            borderRadius: '50%',
                            padding: 2,
                          }}
                        >
                          <PresenceDot userId={user.userId} size={12} />
                        </Box>
                      </Box>

                      {/* User info */}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {`${user.firstName || ''} ${user.surname || ''}`.trim() || user.email}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {user.email}
                        </Text>
                      </Box>

                      {/* Status badge */}
                      <Badge
                        size="xs"
                        color={
                          user.status === 'online'
                            ? 'green'
                            : user.status === 'away'
                              ? 'yellow'
                              : user.status === 'busy'
                                ? 'red'
                                : 'gray'
                        }
                        variant="light"
                      >
                        {user.status}
                      </Badge>
                    </Group>
                  </Paper>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Stack>
    </Paper>
  );
});

/**
 * Compact online users count badge
 */
export function OnlineUsersCount() {
  const { getAllPresence } = usePresence();
  const { presence: presenceClient } = useCCMS();
  const [count, setCount] = useState(0);

  // Initial count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const allPresence = await getAllPresence();
        const onlineCount = allPresence.filter((p) => p.isOnline).length;
        setCount(onlineCount);
      } catch (error) {
        setCount(0);
      }
    };

    fetchCount();
  }, [getAllPresence]);

  // Real-time count updates
  useEffect(() => {
    if (!presenceClient) return;

    const handleUserOnline = () => {
      setCount((prev) => prev + 1);
    };

    const handleUserOffline = () => {
      setCount((prev) => Math.max(0, prev - 1));
    };

    presenceClient.on('userOnline', handleUserOnline);
    presenceClient.on('userOffline', handleUserOffline);

    return () => {
      presenceClient.off('userOnline', handleUserOnline);
      presenceClient.off('userOffline', handleUserOffline);
    };
  }, [presenceClient]);

  return (
    <Badge size="sm" color="green" variant="light" leftSection={<IconUsers size={12} />}>
      {count} online
    </Badge>
  );
}
